import React, { useState, useEffect, useMemo } from 'react';
import LocationList from '../../LocationList';
import ModeHelpTooltip from './components/ModeHelpTooltip';
import RouteEditor from './components/RouteEditor';
import { useTp } from '../../../context/TpContext';
import '../../styles/modes/customMode.css';

// Quick distance calc - good enough for TP visualization
const manhattanDistance = (lat1, lon1, lat2, lon2) => {
  const latDiff = Math.abs(lat2 - lat1) * 111; // rough km conversion
  const lngDiff = Math.abs(lon2 - lon1) * 111;
  return latDiff + lngDiff;
};

export default function CustomMode() {
  const {
    locations, setLocations,
    customPath, setPath, setCustomPath,
    setMapPointSelect, clearMapPointSelect,
    handleUpdateVisiblePath,
    // Route storage
    customRoutes, saveCustomRoutes, getStoredRoutes,
  } = useTp();

  const [showHelp, setShowHelp] = useState(false);
  const [routes, setRoutes] = useState([]);
  
  // TODO: maybe add route validation later?

  // Restore routes when component mounts or when we have locations
  useEffect(() => {
    // Only try to restore if we have locations and no current routes
    if (locations.length > 0 && routes.length === 0) {
      const storedRoutes = getStoredRoutes('Custom Path');
      
      if (storedRoutes && storedRoutes.routes && storedRoutes.routes.length > 0) {
        // Only restore if locations haven't changed
        // FIXME: this JSON comparison is a bit hacky but works for now
        const locationIds = locations.map(loc => loc.id).sort();
        const storedLocationIds = storedRoutes.locations.map(loc => loc.id).sort();
        
        if (JSON.stringify(locationIds) === JSON.stringify(storedLocationIds)) {
          setRoutes(storedRoutes.routes);
        }
      }
    }
  }, [locations, routes.length, getStoredRoutes]);

  // Track supply vs demand for validation
  const supplyDemandBalance = useMemo(() => {
    // Could probably optimize this but it's fast enough
    const totalSupply = locations
      .filter(loc => (loc.tpType || '').toLowerCase() === 'supply')
      .reduce((sum, loc) => sum + Number(loc.quantity || 0), 0);
    
    const totalDemand = locations
      .filter(loc => (loc.tpType || '').toLowerCase() === 'demand')
      .reduce((sum, loc) => sum + Number(loc.quantity || 0), 0);
    
    const difference = totalSupply - totalDemand;
    const isBalanced = difference === 0;
    
    return {
      totalSupply,
      totalDemand,
      difference,
      isBalanced,
      hasExcess: difference > 0,
      hasShortage: difference < 0
    };
  }, [locations]);

  // Save routes on cleanup
  useEffect(() => {
    return () => {
      if (routes.length > 0) {
        saveCustomRoutes(routes, locations);
      }
    };
  }, [routes, locations, saveCustomRoutes]);

  // Show cargo flows on map
  const updateRouteVisualization = (routes) => {
    if (!routes?.length) {
      handleUpdateVisiblePath(null);
      return;
    }
    
    const features = [];
    
    routes.forEach((route) => {
      if (!route.customerAssignments?.length) return; // skip empty routes
      
      const warehouse = locations.find(loc => loc.id === route.warehouseId);
      if (!warehouse) return; // shouldn't happen but just in case
        
        route.customerAssignments.forEach((assignment) => {
          const customer = locations.find(loc => loc.id === assignment.customerId);
          if (!customer) return;
          
          const flowFeature = {
            type: 'Feature',
            properties: {
              route_color: route.color,
              pathType: 'custom',
              flowType: 'warehouse-to-customer',
              warehouse_name: warehouse.number || warehouse.name,
              customer_name: customer.number || customer.name,
              quantity: assignment.assignedQty,
              warehouse_id: route.warehouseId,
              customer_id: assignment.customerId
            },
            geometry: {
              type: 'LineString',
              coordinates: [
                [Number(warehouse.longitude), Number(warehouse.latitude)],
                [Number(customer.longitude), Number(customer.latitude)]
              ]
            }
          };
          
          features.push(flowFeature);
        });
    });
      
    const geoJSONData = {
      type: 'FeatureCollection',
      features: features
    };
      
    handleUpdateVisiblePath(geoJSONData);
  };

  // Map click → toggle last / append id
  useEffect(() => {
    setMapPointSelect((idOrObj) => {
      const id = typeof idOrObj === 'string' ? idOrObj : idOrObj?.id;
      if (!id) return;
      const cur = Array.isArray(customPath) ? customPath : [];
      const last = cur[cur.length - 1];
      const lastId = typeof last === 'string' ? last : last?.id;
      if (lastId === id) setPath(cur.slice(0, -1));
      else setPath([...cur, id]); // store only ids
    });
    return () => clearMapPointSelect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customPath]);

  const handleRouteVisualizationChange = (newVisibleRoutes) => {
    updateRouteVisualization(newVisibleRoutes);
  };

  const handleConfirmRoutes = (routeData) => {
    
    // Prepare data for comparison mode
    const features = [];
    
    routeData.routes.forEach((route, routeIndex) => {
      if (!route.customerAssignments || route.customerAssignments.length === 0) return;
      
      const warehouse = locations.find(loc => loc.id === route.warehouseId);
      if (!warehouse) return;
      
      // Create separate flow feature for each customer assignment
      route.customerAssignments.forEach((assignment, assignmentIndex) => {
        const customer = locations.find(loc => loc.id === assignment.customerId);
        if (!customer) return;
        
        // Calculate distance for this specific flow using Manhattan distance (same as CustomMode)
        const distance = manhattanDistance(
          warehouse.latitude, warehouse.longitude,
          customer.latitude, customer.longitude
        );
        
        const flowFeature = {
          type: 'Feature',
          properties: {
            route_color: route.color,
            distance: distance,
            pathType: 'custom',
            flowType: 'warehouse-to-customer',
            warehouse_name: warehouse.number || warehouse.name,
            customer_name: customer.number || customer.name,
            supply_number: warehouse.number || warehouse.name,
            demand_number: customer.number || customer.name,
            quantity: assignment.assignedQty,
            warehouse_id: route.warehouseId,
            customer_id: assignment.customerId,
            supplyIndex: routeIndex,
            demand_index: assignmentIndex
          },
          geometry: {
            type: 'LineString',
            coordinates: [
              [Number(warehouse.longitude), Number(warehouse.latitude)],
              [Number(customer.longitude), Number(customer.latitude)]
            ]
          }
        };
        
        features.push(flowFeature);
      });
    });
    
    const geoJSONData = {
      type: 'FeatureCollection',
      features: features,
      _isConfirmed: true  // Mark as confirmed for comparison mode visibility
    };
    
    // Set to customPath for comparison mode (VRP-style)
    setCustomPath(geoJSONData);
  };

  return (
    <div className="custom-mode-container">
      {/* Title + help */}
      <div className="mode-description">
        <div className="mode-title-container">
          <h3>Custom Path</h3>
          <button className="mode-help-button" onClick={() => setShowHelp(true)} title="Help">?</button>
        </div>
        <p>Click markers to build a sequence. The orange dashed line updates. Distance uses calculation methods.</p>
      </div>
      {showHelp && <ModeHelpTooltip onClose={() => setShowHelp(false)} />}

      {/* Supply-Demand Balance Alert */}
      {!supplyDemandBalance.isBalanced && (
        <div className={`balance-alert ${supplyDemandBalance.hasExcess ? 'excess' : 'shortage'}`}>
          <div className="alert-content">
            <div className="alert-text">
              <strong>Unbalanced Problem:</strong> Total Supply ({supplyDemandBalance.totalSupply}) ≠ Total Demand ({supplyDemandBalance.totalDemand})
              <br />
              <span className="alert-hint">You can still edit routes - {supplyDemandBalance.hasExcess ? 'some warehouses may have unused capacity' : 'some customer demand may remain unmet'}.</span>
      </div>
        </div>
        </div>
      )}

      {/* Points list */}
        <LocationList
          locations={locations}
          mode="Custom Path"
        readOnly={true}
        showActions={false}
        allowDelete={false}
        onLocationChange={setLocations}
      />

      {/* Route Editor */}
      <RouteEditor
        locations={locations}
        initialRoutes={routes}
        onRouteChange={(newRoutes) => {
          setRoutes(newRoutes);
          // Auto-save routes to memory when they change
          saveCustomRoutes(newRoutes, locations);
        }}
        onRouteVisualizationChange={handleRouteVisualizationChange}
        onConfirmRoutes={handleConfirmRoutes}
      />
    </div>
  );
}